import { UTCDate } from '@date-fns/utc';
import { styled } from '@mui/material';
import React from 'react';
import CalendarHeader from './CalendarHeader';
import CalendarMonthSelector from './CalendarMonthSelector';
import CalendarMonthViewport from './CalendarMonthViewport';
import { calendarLayout } from './constants';
import { CalendarDateRange, DateRange, DateRangePickerVariant } from './types';
import {
    formatCalendarRange,
    parseCalendarDate,
    parseCalendarRange,
} from './dateOnly';
import {
    clampMonth,
    getInitialMonth,
    getVisibleYears,
    isMonthSelectable,
    shiftMonth,
    Month,
} from './utils';
import { useCalendarSelection } from './useCalendarSelection';

const Base = styled('div')(({ theme }) => ({
    'display': 'flex',
    'flexDirection': 'column',
    'gap': theme.spacing(calendarLayout.contentGap),
    'width': 'fit-content',
    'minWidth': calendarLayout.minWidth,
    'padding': theme.spacing(calendarLayout.contentGap),
    // The month grid remains readable while leaving a gutter on narrow phones.
    '@media (max-width: 347px)': {
        minWidth: calendarLayout.monthViewportWidth + 16,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

interface CalendarProps {
    periods: DateRange[];
    onChange: (periods: DateRange[]) => void;
    multiplePeriods?: boolean;
    disableBefore?: string;
    disableAfter?: string;
    disabledDates?: string[];
    initialMonth?: Month;
    onSelectionComplete?: () => void;
    variant?: DateRangePickerVariant;
}

export const Calendar = React.forwardRef<HTMLDivElement, CalendarProps>(
    (
        {
            periods: periodValues,
            onChange,
            multiplePeriods = false,
            disableBefore: disableBeforeValue,
            disableAfter: disableAfterValue,
            disabledDates: disabledDateValues,
            initialMonth,
            onSelectionComplete,
            variant = 'range',
        },
        ref
    ) => {
        const periods = React.useMemo(
            () =>
                periodValues
                    .map(parseCalendarRange)
                    .filter(
                        (range): range is CalendarDateRange => range !== null
                    ),
            [periodValues]
        );
        const disableBefore = React.useMemo(
            () =>
                disableBeforeValue
                    ? (parseCalendarDate(disableBeforeValue) ?? undefined)
                    : undefined,
            [disableBeforeValue]
        );
        const disableAfter = React.useMemo(
            () =>
                disableAfterValue
                    ? (parseCalendarDate(disableAfterValue) ?? undefined)
                    : undefined,
            [disableAfterValue]
        );
        const disabledDates = React.useMemo(
            () =>
                disabledDateValues
                    ?.map(parseCalendarDate)
                    .filter((date): date is UTCDate => date !== null),
            [disabledDateValues]
        );
        const handleChange = React.useCallback(
            (ranges: CalendarDateRange[]) =>
                onChange(ranges.map(formatCalendarRange)),
            [onChange]
        );
        const animationDuration = calendarLayout.transitionDurationMs;
        const [month, setMonth] = React.useState<Month>(() =>
            getInitialMonth({
                periods,
                initialMonth,
                disableBefore,
                disableAfter,
            })
        );
        const [isSelectorOpen, setIsSelectorOpen] = React.useState(false);
        const [selectorYear, setSelectorYear] = React.useState(month.year);
        const [transitionMonth, setTransitionMonth] =
            React.useState<Month | null>(null);
        const [transitionDirection, setTransitionDirection] = React.useState<
            'next' | 'prev'
        >('next');
        const yearRefs = React.useRef<Record<number, HTMLButtonElement | null>>(
            {}
        );

        React.useEffect(() => {
            setMonth(currentMonth =>
                clampMonth(currentMonth, disableBefore, disableAfter)
            );
        }, [disableAfter, disableBefore]);

        React.useEffect(() => {
            if (!isSelectorOpen) {
                return;
            }

            yearRefs.current[selectorYear]?.scrollIntoView({
                block: 'center',
                behavior: 'instant',
            });
        }, [isSelectorOpen, selectorYear]);

        const {
            displayedPeriods,
            previewPeriod,
            handleDayClick,
            handleDayHover,
        } = useCalendarSelection({
            periods,
            variant,
            onChange: handleChange,
            multiplePeriods,
            onSelectionComplete,
        });

        const years = React.useMemo(
            () => getVisibleYears(month.year, disableBefore, disableAfter),
            [disableAfter, disableBefore, month.year]
        );

        const previousMonth = React.useMemo(
            () => shiftMonth(month, -1),
            [month]
        );
        const nextMonth = React.useMemo(() => shiftMonth(month, 1), [month]);

        const canGoPrevious = isMonthSelectable(
            previousMonth.year,
            previousMonth.month,
            disableBefore,
            disableAfter
        );
        const canGoNext = isMonthSelectable(
            nextMonth.year,
            nextMonth.month,
            disableBefore,
            disableAfter
        );

        React.useEffect(() => {
            if (!transitionMonth) {
                return;
            }

            const timeout = window.setTimeout(() => {
                setTransitionMonth(null);
            }, animationDuration);

            return () => window.clearTimeout(timeout);
        }, [animationDuration, transitionMonth]);

        const handleSetMonth = React.useCallback(
            (nextMonth: Month) => {
                const clampedNextMonth = clampMonth(
                    nextMonth,
                    disableBefore,
                    disableAfter
                );

                if (
                    clampedNextMonth.year === month.year &&
                    clampedNextMonth.month === month.month
                ) {
                    return;
                }

                setTransitionDirection(
                    clampedNextMonth.year > month.year ||
                        (clampedNextMonth.year === month.year &&
                            clampedNextMonth.month > month.month)
                        ? 'next'
                        : 'prev'
                );
                setTransitionMonth(month);
                setMonth(clampedNextMonth);
            },
            [disableAfter, disableBefore, month]
        );

        const handleToggleSelector = React.useCallback(() => {
            setIsSelectorOpen(currentState => {
                if (!currentState) {
                    setSelectorYear(month.year);
                }

                return !currentState;
            });
        }, [month.year]);

        return (
            <Base ref={ref}>
                <CalendarHeader
                    month={month}
                    isSelectorOpen={isSelectorOpen}
                    canGoPrevious={canGoPrevious}
                    canGoNext={canGoNext}
                    onToggleSelector={handleToggleSelector}
                    onPrevious={() => handleSetMonth(previousMonth)}
                    onNext={() => handleSetMonth(nextMonth)}
                />

                {isSelectorOpen ? (
                    <CalendarMonthSelector
                        years={years}
                        selectorYear={selectorYear}
                        month={month}
                        isMonthSelectable={monthIndex =>
                            isMonthSelectable(
                                selectorYear,
                                monthIndex,
                                disableBefore,
                                disableAfter
                            )
                        }
                        onSelectYear={setSelectorYear}
                        onSelectMonth={monthIndex => {
                            handleSetMonth({
                                year: selectorYear,
                                month: monthIndex,
                            });
                            setIsSelectorOpen(false);
                        }}
                        setYearButtonRef={(year, element) => {
                            yearRefs.current[year] = element;
                        }}
                    />
                ) : (
                    <CalendarMonthViewport
                        month={month}
                        transitionMonth={transitionMonth}
                        transitionDirection={transitionDirection}
                        variant={variant}
                        periods={displayedPeriods}
                        previewPeriod={previewPeriod}
                        disableBefore={disableBefore}
                        disableAfter={disableAfter}
                        disabledDates={disabledDates}
                        onDayClick={handleDayClick}
                        onDayHover={handleDayHover}
                    />
                )}
            </Base>
        );
    }
);

Calendar.displayName = 'Calendar';

export default Calendar;
